import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'

import CustomSwitch from './switch'
import loadPredictions from '../kyc/personal-info/loadPredictions'
import ResultsFound from '../kyc/personal-info/helpers/results-found'
import FindingResults from '../kyc/personal-info/helpers/finding-results'
import ModalGoogleMaps from '../kyc/personal-info/modal'

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: 'Nunito';
  font-size: 15px;
  font-weight: 600;
  color: #1f2425;
`

const BaseInput = styled.input<{ error?: boolean }>`
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  padding: 10px;
  height: 56px;
  font-family: 'Nunito';
  font-size: 15px;
  &:focus {
    outline: none;
  }
  ${({ error }) =>
    error &&
    `
    border-color: #d32f2f;
    &:focus {
      border-color: #d32f2f;
    }
  `}
`

const AddressInput = styled(BaseInput)`
  width: 100%;
`

const BlockInput = styled(BaseInput)`
  width: 90px;
  height: 56px;
  padding: 10px;
  border-radius: 5px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const AddressWrapper = styled.div`
  position: relative;
  width: 100%;
`


const ManualAddressEntry = styled.div`
  background: #000;
  color: #FFF;
  padding: 10px;
  cursor: pointer;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.015;
  line-height: 20px;
`

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Title = styled.h2`
  font-family: 'Nunito';
  font-size: 24px;
  font-weight: 600;
  color: #1f2425;
  margin-bottom: 4px;
`

const ExplanatoryText = styled.p`
  font-family: 'Nunito';
  font-size: 15px;
  font-weight: 400;
  color: #1f2425;
  margin-bottom: 16px;
  margin-top: 4px;
`

interface SwitchWithFieldsProps {
  useTaxAddress: boolean
  taxAddress: string
  taxDepto: string
  taxCity: string
  taxPostalCode: string
  onChange: (field: string, value: any) => void
  countryCode?: string
  errors?: { [key: string]: string }
  touched?: { [key: string]: boolean }
}

const SwitchWithFields: React.FC<SwitchWithFieldsProps> = ({
  useTaxAddress,
  taxAddress,
  taxDepto,
  taxCity,
  taxPostalCode,
  onChange,
}) => {

  const [address, setAddress] = useState<string>("")
  const [streetNumber, setStreetNumber] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [postalCode, setPostalCode] = useState<string>("")
  const [countryCode, setCountryCode] = useState<string>("CL")
  const [latitude, setLatitude] = useState<number>(0)
  const [longitude, setLongitude] = useState<number>(0)
  const [notSelected, setNotSelected] = useState<boolean>(true)
  const [gmapsResults, setGmapResults] = useState<any>([])
  const [addressError, setAddressError] = useState<string>("")
  const [showModal, setShowModal] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const timeoutRef = useRef<number>()

  useEffect(() => {
    if (!useTaxAddress) {
      onChange('taxCity', city)
      onChange('taxPostalCode', postalCode)
      onChange('taxLatitude', latitude)
      onChange('taxLongitude', longitude)
      onChange('taxStreetNumber', streetNumber)
    }
  }, [city, postalCode, latitude, longitude, streetNumber, useTaxAddress])

  useEffect(() => {
    if (useTaxAddress) {
      setAddress('')
      setStreetNumber('')
      setCity('')
      setPostalCode('')
      setCountryCode('CL')
      setLatitude(0)
      setLongitude(0)
      setGmapResults([])
      setError(false)
      setAddressError('')
    }
  }, [useTaxAddress])

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setAddress(value)
    onChange('taxAddress', {
      address: value,
      streetNumber,
      city,
      postalCode,
      countryCode,
      latitude,
      longitude
    })
    onChange('taxCity', city);
    onChange('taxPostalCode', postalCode);
    setAddressError('')

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(async () => {
      if (value.length > 3 && notSelected) {
        try {
          const { result, error } = await loadPredictions({
            countryCode,
            value: value,
          })
          if (!error) {
            setGmapResults(result)
            setError(false)
          } else {
            setError(true)
          }
          if (!/\d/.test(value)) {
            setError(true)
          }
        } catch (error) {
          console.error('Error loading predictions:', error)
          setError(true)
        }
      }
    }, 1000)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 91 || event.keyCode === 32) return
    setStreetNumber('')
    setGmapResults([])
    setNotSelected(true)
    setAddress(event.currentTarget.value)
    setError(false)
    setCity('')
    onChange('taxAddress', event.currentTarget.value)
  }

  return (
    <Container>
      <CustomSwitch
        form={{
          values: { useTaxAddress, taxAddress, taxDepto, taxCity, taxPostalCode },
          setFieldValue: onChange,
        }}
        field={{
          name: 'useTaxAddress',
          value: typeof useTaxAddress === 'boolean' ? useTaxAddress : false
        }}
      />
      {!useTaxAddress && (
        <FieldsContainer>
          <Title>Dirección Tributaria</Title>
          <ExplanatoryText>
            Si tienes una dirección tributaria diferente, agrégala aquí
          </ExplanatoryText>
          <AddressWrapper>
            <Label>Dirección</Label>
            <AddressInput
              name="taxAddress"
              placeholder="Calle y número"
              value={address}
              onChange={handleAddressChange}
              onKeyDown={handleKeyDown}
              error={!!addressError}
            />
            {gmapsResults.length === 0 && city.length === 0 && address.length > 0 && notSelected && !error ? (
              <FindingResults>Buscando...</FindingResults>
            ) : (
              <div className="wrapper-results">
                <ResultsFound
                  results={gmapsResults}
                  addressField="taxAddress"
                  setter={{
                    setAddressError,
                    setFieldValue: onChange,
                    setGmapResults,
                    setNotSelected,
                    setAddress,
                    setLatitude,
                    setLongitude,
                    setPostalCode,
                    setStreetNumber,
                    setCity,
                    setShowModal,
                    setError,
                  }}
                />
              </div>
            )}
            {address.length > 0 && error && (
              <ManualAddressEntry
                onClick={() => setShowModal(true)}
              >
                Ingresa tu dirección manualmente
              </ManualAddressEntry>
            )}
            {showModal && (
              <ModalGoogleMaps
                setShowModal={setShowModal}
                setFieldValue={onChange}
                address={address}
                city={city}
                setCity={setCity}
                setAddress={setAddress}
                values={{ useTaxAddress, taxAddress, taxDepto, taxCity, taxPostalCode }}
                streetNumber={streetNumber}
                setStreetNumber={setStreetNumber}
                setError={setError}
                setGmapResults={setGmapResults}
                setAddressError={setAddressError}
              />
            )}
          </AddressWrapper>
          <div>
            <Label>Block/Depto/Casa fiscal (opcional)</Label>
            <BlockInput
              name="addressExtension"
              value={taxDepto}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('taxDepto', e.target.value)}
            />
          </div>
        </FieldsContainer>
      )}
    </Container>
  )
}

export default SwitchWithFields
